<template>
  <div>
    <div v-if="physicalErrorMsg" class="app-alert-error app-system-alert">
      <div ref="alertMessage" tabindex="-1" class="bannerAlert"> 
        Physical Address - {{ physicalErrorMsg }}
      </div>  
    </div>
    <div v-if="mailingErrorMsg" class="app-alert-error app-system-alert">
      <div ref="alertMessage" tabindex="-1" class="bannerAlert"> 
        Mailing Address - {{ mailingErrorMsg }}
      </div> 
    </div>
    <div>
      <p>Your existing address is displayed below. To clear the existing address click 'Clear'. </p>
      <p>Please update your address and click on 'Next'. You can only update to an US Domestic address. The system will validate the address and may suggest a standardized address to ensure address accuracy that aids in timely mail delivery.</p>
      <button class="btnClear" @click="clearAll">
        Clear
      </button>
      <div class="phymailcheck">
        <input type="checkbox" id="address" v-model="physicalMailingAddressCheck" @change="checkMailingAddress()">
        <label for="address">My mailing address is same as my physical address</label>
      </div>
      <div class="app-address-info">
        <div>
          <h2 for="physicalAddress" aria-label="Physical address required" class="app-required-indicator __after">Physical Address</h2>
          <div class="app-input-address">
            <div class="app-user-address">
              <div class="add-line">
                <label for="phyaddressline1" class="sr-only">Address line 1</label>
                <input type="text" class="user-address-info" id="phyaddressline1" placeholder="Address line 1 *" v-model="physicalAddress1" @focusout="checkMailingAddress()"
                  @input="validateAddress">
                <p v-if="physicalAddress1ErrMsg" class="err-msg">{{ physicalAddress1ErrMsg }}</p>
              </div>
            </div>
            <div class="app-user-address">
              <div class="add-line">
                <label for="phyaddressline2" class="sr-only">Address line 2</label>
                <input type="text" placeholder="Address line 2" id="phyaddressline2" class="user-address-info" v-model="physicalAddress2" @focusout="checkMailingAddress()">
              </div>
            </div>
            <div class="app-user-address">
              <div v-if="physicalAddressState != 'RI' && physicalAddressState != 40">
                <label for="physicalAddressCity" class="sr-only">City</label>
                <input type="text" placeholder="City *" id="physicalAddressCity" class="user-address-info-city" v-model="physicalAddressCity" @focusout="checkMailingAddress()"
                  @input="validateCity">
                <p v-if="physicalAddressCityErrMsg" class="err-msg">{{ physicalAddressCityErrMsg }}</p>
              </div>
              <div v-else>
                <label for="physicalAddressCity" class="sr-only">Select a city</label>
                <select class="app-custom-select" id="physicalAddressCity" aria-required="true" v-model="physicalAddressCity" @focusout="checkMailingAddress()"
                @change="validateCity">
                  <option v-for="(type) in crashLocations" :value="type.description" :key="type.description">
                    {{ type.description == '' ? 'Select a city' : type.description }}
                  </option>
                </select>
                <p v-if="physicalAddressCityErrMsg" class="err-msg">{{ physicalAddressCityErrMsg }}</p>
              </div>
            </div>
            <div class="app-user-address-info">
              <div class="app-user-address">
                <div>
                  <label for="physicalAddressState" class="sr-only">Select state</label>
                  <select class="app-custom-select" id="physicalAddressState" aria-required="true" v-model="physicalStateId" @focusout="checkMailingAddress()"
                    @change="changeState">
                    <option value=''>Select state *</option>
                    <option v-for="(type) in usStates" :value="type.stateId" :key="type.stateId" :title="type.code">
                      {{ type.code }} - {{ type.description }} 
                    </option>
                  </select>
                  <p v-if="physicalAdressStateErrMsg" class="err-msg">{{ physicalAdressStateErrMsg }}</p>
                </div>
              </div>
              <div>
                <div class="app-user-address">
                  <div v-if="physicalAddressState != 'RI' && physicalAddressState != 40">
                    <label for="physicalAddressZip" class="sr-only">Zipcode</label>
                    <input type="text" placeholder="Zip code *" id="physicalAddressZip" class="user-address-info-zip" 
                      oninput="this.value = this.value.replace(/[^0-9]/g, '')" maxlength="5" v-model="physicalAddressZip" @focusout="checkMailingAddress()" @input="validateZip">
                  </div>
                  <div v-else>
                    <label for="physicalAddressZip" class="sr-only">Select a zipcode</label>
                    <select class="app-custom-select zip-selection" id="physicalAddressZip" aria-required="true" v-model="physicalAddressZip"
                      @focusout="checkMailingAddress()" @change="validateZip">
                      <option v-for="(type) in instateZipCodes" :value="type.zipCode" :key="type.zipId">
                        {{ type.zipCode == '' ? 'Zipcode' : type.zipCode }}
                      </option>
                    </select>
                  </div>
                  <div class="diff">-</div>
                  <div>
                    <label for="physicalAddressZipplus" class="sr-only">Zipplus</label>
                    <input type="text" placeholder="Zip plus" class="user-address-info-zipplus" id="physicalAddressZipplus"
                      oninput="this.value = this.value.replace(/[^0-9]/g, '')" maxlength="4" v-model="physicalAddressZipPlus"  @focusout="checkMailingAddress()">
                  </div>
                </div>
                <p v-if="physicalAddressZipErrMsg" class="err-msg zip-error">{{ physicalAddressZipErrMsg }}</p>
              </div>
            </div>
          </div>
        </div>
        <div>
          <h2 for="mailingAddress" aria-label="Mailing address required" class="app-required-indicator __after">Mailing Address</h2>
          <div class="app-input-address">
            <div class="app-user-address">
              <div class="add-line">
                <label for="mailingaddressline1" class="sr-only">Address line 1</label>
                <input type="text" placeholder="Address line 1 *" id="mailingaddressline1" class="user-address-info" :class="{ 'user-address-info-gray': physicalMailingAddressCheck }"
                  v-model="mailingAddress1" :readonly="physicalMailingAddressCheck" :disabled="physicalMailingAddressCheck" @input="validateMailingAddress">
                <p v-if="mailingAddress1ErrMsg" class="err-msg">{{ mailingAddress1ErrMsg }}</p>
              </div>
            </div>
            <div class="app-user-address">
              <div class="add-line">
                <label for="mailingaddressline2" class="sr-only">Address line 2</label>
                <input type="text" placeholder="Address line 2" id="mailingaddressline2" class="user-address-info" :class="{ 'user-address-info-gray': physicalMailingAddressCheck }"
                  v-model="mailingAddress2" :readonly="physicalMailingAddressCheck" :disabled="physicalMailingAddressCheck">
              </div>
            </div>
            <div class="app-user-address">
              <div v-if="mailingAddressState != 'RI'&& mailingAddressState != 40">
                <label for="mailingAddressCity" class="sr-only">City</label>
                <input type="text" placeholder="City *" id="mailingAddressCity" class="user-address-info-city" :class="{ 'user-address-info-gray': physicalMailingAddressCheck }"
                  v-model="mailingAddressCity" :readonly="physicalMailingAddressCheck" :disabled="physicalMailingAddressCheck" @input="validateMailingCity">
                <p v-if="mailingAddressCityErrMsg" class="err-msg">{{ mailingAddressCityErrMsg }}</p>
              </div>
              <div v-else>
                <label for="mailingAddressCity" class="sr-only">Select a city</label>
                <select class="app-custom-select" aria-required="true" id="mailingAddressCity" :class="{ 'user-address-info-gray': physicalMailingAddressCheck }"
                  v-model="mailingAddressCity" :readonly="physicalMailingAddressCheck" :disabled="physicalMailingAddressCheck" @change="validateMailingCity">
                  <option v-for="(type) in crashLocations" :value=" type.description" :key="type.cityId">
                    {{ type.description == '' ? 'Select a city' : type.description }}
                  </option>
                </select>
                <p v-if="mailingAddressCityErrMsg" class="err-msg">{{ mailingAddressCityErrMsg }}</p>
              </div>
            </div>
            <div class="app-user-address-info">
              <div class="app-user-address">
                <div>   
                  <label for="mailingAddressState" class="sr-only">Select state</label>       
                  <select class="app-custom-select" aria-required="true" id="mailingAddressState" :class="{ 'user-address-info-gray': physicalMailingAddressCheck }"
                    v-model="mailingStateId" :readonly="physicalMailingAddressCheck" :disabled="physicalMailingAddressCheck" @change="changeMailingState">
                    <option value=''>Select state *</option>
                    <option v-for="(type) in usStates" :value="type.stateId" :key="type.stateId" :title="type.code">
                      {{ type.code }} - {{ type.description }}
                    </option>
                  </select>
                  <p v-if="mailingAdressStateErrMsg" class="err-msg">{{ mailingAdressStateErrMsg }}</p>
                </div>
              </div>
              <div>
                <div class="app-user-address">
                  <div v-if="mailingAddressState != 'RI' && mailingAddressState != 40">
                    <label for="mailingAddressZip" class="sr-only">Zipcode</label>
                    <input type="text" placeholder="Zip code *" id="mailingAddressZip" class="user-address-info-zip" :class="{ 'user-address-info-gray': physicalMailingAddressCheck }"
                      oninput="this.value = this.value.replace(/[^0-9]/g, '')" maxlength="5" v-model="mailingAddressZip" :readonly="physicalMailingAddressCheck"
                      :disabled="physicalMailingAddressCheck" @input="validateMailingZip">
                  </div>
                  <div v-else>
                    <label for="mailingAddressZip" class="sr-only">Select a zipcode</label>
                    <select class="app-custom-select zip-selection" aria-required="true"  id="mailingAddressZip"
                      :class="{ 'user-address-info-gray': physicalMailingAddressCheck }" v-model="mailingAddressZip" 
                      :readonly="physicalMailingAddressCheck" :disabled="physicalMailingAddressCheck" @change="validateMailingZip">
                      <option v-for="(type) in instateZipCodes" :value="type.zipCode" :key="type.zipId">
                        {{ type.zipCode =='' ? 'Zipcode' : type.zipCode }}
                      </option>
                    </select> 
                  </div>
                  <div class="diff">-</div> 
                  <div>
                    <label for="mailingAddressZipplus" class="sr-only">Zipplus</label>
                    <input type="text" placeholder="Zip plus" id="mailingAddressZipplus" oninput="this.value = this.value.replace(/[^0-9]/g, '')" maxlength="4"
                      class="user-address-info-zipplus" :class="{ 'user-address-info-gray': physicalMailingAddressCheck }" v-model="mailingAddressZipPlus"
                      :readonly="physicalMailingAddressCheck" :disabled="physicalMailingAddressCheck">
                  </div>         
                </div>
                <p v-if="mailingAddressZipErrMsg" class="err-msg zip-error">{{ mailingAddressZipErrMsg }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <BaseButtonGroup>
      <button type="button" @click="handleRouting('previous')">Previous</button>
      <!-- <button type="submit" class="app-btn-primary" @click.prevent="handleRouting('next')">
        Next
      </button> -->
      <CustomButton @click="handleRouting('next')">
        Next
      </CustomButton>
    </BaseButtonGroup>
  </div>
</template>

<script>
import * as COMMONS from '../../store/_commons';
import { mapGetters, mapActions } from 'vuex';
import BaseButtonGroup from '@/components/ui/BaseButtonGroup';
import CustomButton from '../CustomButton.vue';

export default {
  name: 'AddressUpdate',
  components: {
    BaseButtonGroup,
    CustomButton
  },
  data() {
    return {
      physicalAddress1: '',
      physicalAddress2: '',
      physicalAddressCity: '',
      physicalAddressState: '',
      physicalAddressZip: '',
      physicalAddressZipPlus: '',

      mailingAddress1:'',
      mailingAddress2: '',
      mailingAddressCity: '',
      mailingAddressState: '',
      mailingAddressZip: '',
      mailingAddressZipPlus: '',

      physicalMailingAddressCheck: true,

      physicalAddress1ErrMsg: '',
      physicalAddressCityErrMsg: '',
      physicalAdressStateErrMsg: '',
      physicalAddressZipErrMsg: '',

      mailingAddress1ErrMsg: '',
      mailingAddressCityErrMsg: '',
      mailingAdressStateErrMsg: '',
      mailingAddressZipErrMsg: ''
    }
  },
  computed: {
    ...mapGetters({
      personalInfo: COMMONS.GUEST_PERSONAL_INFO,
      crashLookupInfo: COMMONS.GUEST_LOGIN_LOOKUP_INFO,
      addressLookupInfo: COMMONS.GUEST_ADDRESS_LOOKUP_INFO 
    }),

    checkSameAddress() {
      return (this.physicalAddress1 == this.mailingAddress1) && (this.physicalAddress2 == this.mailingAddress2) && (this.physicalAddressCity == this.mailingAddressCity) 
              && (this.physicalStateId == this.mailingStateId) && (this.physicalAddressZip == this.mailingAddressZip) && (this.physicalAddressZipPlus == this.mailingAddressZipPlus);
    },

    crashLocations() {
      let sortCityName = []
      if(this.crashLookupInfo.allCrashLocation) {
        sortCityName = this.crashLookupInfo.allCrashLocation.slice().sort((a,b) => {
          return a.description.localeCompare(b.description); 
        });
      }
      return sortCityName;
    },
    usStates() {     
      return this.addressLookupInfo.allUsStates;
    },
    instateZipCodes() {
      return this.addressLookupInfo.allInstateZipcodes;
    },
    physicalStateId: {
      get() {
        if(this.addressLookupInfo && this.addressLookupInfo.allUsStates) {
          let allUsStates = this.addressLookupInfo.allUsStates;
          let stateCode = this.physicalAddressState;
        
          let stateArray = allUsStates.filter(function(val) {
            return stateCode == val.code || stateCode == val.stateId ;
          })      
          return stateArray&&stateArray.length > 0 ? stateArray[0].stateId : '';
        }  
        else {
          return '';
        }
      },
      set(newVal) {
        return this.physicalAddressState = newVal;
      }

    },
    mailingStateId: {
      get() {
        if(this.addressLookupInfo && this.addressLookupInfo.allUsStates) {
          let allUsStates = this.addressLookupInfo.allUsStates;
          let stateCode = this.mailingAddressState;
          let stateArray = allUsStates.filter(function(val) {
            return stateCode == val.code || stateCode == val.stateId ;
          })
          return stateArray&&stateArray.length > 0 ? stateArray[0].stateId : '';
        }
        else {
          return '';
        }
      },
        
      set(newVal) {
        return this.mailingAddressState = newVal;
      }
    },
    physicalErrorMsg() {
      return this.$store.state.guestLogin.physicalAddressMsg;
    },
    mailingErrorMsg() {
      return this.$store.state.guestLogin.mailingAddressMsg;
    }
  },
  created() {
    this.getCrashLocation();
    this.getUsStates();
    this.getInstateZipCodes();
    if(this.personalInfo.personalInformation) {
      this.physicalAddress1 = this.personalInfo.personalInformation.physicalAddress.addressLine1;
      this.physicalAddress2 = this.personalInfo.personalInformation.physicalAddress.addressLine2;
      this.physicalAddressCity = this.personalInfo.personalInformation.physicalAddress.city.toUpperCase();
      this.physicalAddressState = this.personalInfo.personalInformation.physicalAddress.addressType == 'Us Domestic'? 
      this.personalInfo.personalInformation.physicalAddress.state : this.personalInfo.personalInformation.physicalAddress.province;
      if(this.personalInfo.personalInformation.physicalAddress.addressType == 'Us Domestic') {
        this.physicalAddressZip = this.personalInfo.personalInformation.physicalAddress.zipCode
        this.physicalAddressZipPlus = this.personalInfo.personalInformation.physicalAddress.zipPlus
      } 
      else {
        this.physicalAddressZip = this.personalInfo.personalInformation.physicalAddress.addressType == 'Us Domestic' ? 
        this.personalInfo.personalInformation.physicalAddress.zipPlus == '' ?  this.personalInfo.personalInformation.physicalAddress.zipCode : 
        `${this.personalInfo.personalInformation.physicalAddress.zipCode} - ${this.personalInfo.personalInformation.physicalAddress.zipPlus}`
        : `${this.personalInfo.personalInformation.physicalAddress.internationalZipCode}`;
      }

      this.mailingAddress1 = this.personalInfo.personalInformation.mailingAddress.addressLine1;
      this.mailingAddress2 = this.personalInfo.personalInformation.mailingAddress.addressLine2;
      this.mailingAddressCity = this.personalInfo.personalInformation.mailingAddress.city.toUpperCase();
      this.mailingAddressState = this.personalInfo.personalInformation.mailingAddress.addressType == 'Us Domestic'? 
        this.personalInfo.personalInformation.mailingAddress.state : this.personalInfo.personalInformation.mailingAddress.province;
      if(this.personalInfo.personalInformation.mailingAddress.addressType == 'Us Domestic') {
        this.mailingAddressZip = this.personalInfo.personalInformation.mailingAddress.zipCode
        this.mailingAddressZipPlus = this.personalInfo.personalInformation.mailingAddress.zipPlus
      }
      else{
        this.mailingAddressZip = this.personalInfo.personalInformation.mailingAddress.addressType == 'Us Domestic' ?
        this.personalInfo.personalInformation.mailingAddress.zipPlus == '' ?  this.personalInfo.personalInformation.mailingAddress.zipCode :
        `${this.personalInfo.personalInformation.mailingAddress.zipCode} - ${this.personalInfo.personalInformation.mailingAddress.zipPlus}`
        : `${this.personalInfo.personalInformation.mailingAddress.internationalZipCode}`;
      }
    }
  },
  mounted() {
    window.scroll(0,0);
    document.body.setAttribute("tabindex", "1");
    document.body.focus();

    document.body.removeAttribute("tabindex");
    document.getElementById("app").scrollTop = 0;

    this.physicalMailingAddressCheck = this.checkSameAddress;
  },
  watch: {

  },
  methods: {
    ...mapActions({
      getUpdateAddress: COMMONS.GUEST_UPDATE_ADDRESS,
      getCrashLocation: COMMONS.GUEST_CRASH_LOCATION,
      getUsStates: COMMONS.GUEST_US_STATES,
      getInstateZipCodes: COMMONS.GUEST_INSTATE_ZIPCODES
    }),

    async handleRouting(info) {
      if(info=='previous') {
        this.$router.push({ path : '/profile' });
      }
      else if (info=='next') {
        this.checkMailingAddress();
        this.validateAddress();
        this.validateCity();
        this.validateState();
        this.validateZip();
        this.validateMailingAddress();
        this.validateMailingCity();
        this.validateMailingState();
        this.validateMailingZip();
        if((this.physicalAddress1ErrMsg == null || this.physicalAddress1ErrMsg == '') && (this.physicalAddressCityErrMsg == null || this.physicalAddressCityErrMsg == '') && 
          (this.physicalAdressStateErrMsg == null || this.physicalAdressStateErrMsg == '') && (this.physicalAddressZipErrMsg == null || this.physicalAddressZipErrMsg == '')) {
          if(!this.physicalMailingAddressCheck) {
            if((this.mailingAddress1ErrMsg == null || this.mailingAddress1ErrMsg == '') && (this.mailingAddressCityErrMsg == null || this.mailingAddressCityErrMsg == '') && 
              (this.mailingAdressStateErrMsg == null || this.mailingAdressStateErrMsg == '') && (this.mailingAddressZipErrMsg == null || this.mailingAddressZipErrMsg == '')) {
              let physicalAddressDataObj = {   
                addressCategoryId: 1,        
                addressLine1: this.physicalAddress1.toUpperCase(),
                addressLine2: this.physicalAddress2.toUpperCase(),
                city: this.physicalAddressCity.toUpperCase(),
                stateId: this.physicalStateId,
                zipCode: this.physicalAddressZip,
                zipPlus: this.physicalAddressZipPlus
              }
              let mailingAddressDataObj = {
                addressCategoryId: 2,
                addressLine1: this.mailingAddress1.toUpperCase(),
                addressLine2: this.mailingAddress2.toUpperCase(),
                city: this.mailingAddressCity.toUpperCase(),
                stateId: this.mailingStateId,
                zipCode: this.mailingAddressZip,
                zipPlus: this.mailingAddressZipPlus
              }
              this.$store.state.guestLogin.physicalMailingAddressCheck = this.physicalMailingAddressCheck;
              this.getUpdateAddress(physicalAddressDataObj);
              this.getUpdateAddress(mailingAddressDataObj);
              // this.$store.state.guestLogin.addressUpdateStep = 2;
            }
          }
          else {
            if(this.physicalMailingAddressCheck) {
              let physicalAddressDataObj = {    
                addressCategoryId: 1,       
                addressLine1: this.physicalAddress1.toUpperCase(),
                addressLine2: this.physicalAddress2.toUpperCase(),
                city: this.physicalAddressCity.toUpperCase(),
                stateId: this.physicalStateId,
                zipCode: this.physicalAddressZip,
                zipPlus: this.physicalAddressZipPlus
              }
              let mailingAddressDataObj = {
                addressCategoryId: 2,
                addressLine1: this.mailingAddress1.toUpperCase(),
                addressLine2: this.mailingAddress2.toUpperCase(),
                city: this.mailingAddressCity.toUpperCase(),
                stateId: this.mailingStateId,
                zipCode: this.mailingAddressZip,
                zipPlus: this.mailingAddressZipPlus
              }
              this.$store.state.guestLogin.physicalMailingAddressCheck = this.physicalMailingAddressCheck;
              this.getUpdateAddress(physicalAddressDataObj);
              this.getUpdateAddress(mailingAddressDataObj);
              // this.$store.state.guestLogin.addressUpdateStep = 2;
            }
          }
        }
      }
    },

    changeState(e) {
      if(this.physicalStateId == null || this.physicalStateId == '') {
        this.physicalAdressStateErrMsg = 'State is required';
      }
      else{
        this.physicalAdressStateErrMsg = '';
        this.physicalAddressCity = '';
        this.physicalAddressZip = '';
        this.physicalAddressZipPlus = '';
        this.physicalAddressState = e.target.value;
      }
    },

    changeMailingState(e) {
      if((this.mailingStateId == null || this.mailingStateId == '') && !this.physicalMailingAddressCheck) {
        this.mailingAdressStateErrMsg = 'State is required';
      }
      else{
        this.mailingAdressStateErrMsg = '';
        this.mailingAddressCity = '';
        this.mailingAddressZip = '';
        this.mailingAddressZipPlus = '';
        this.mailingAddressState = e.target.value;
      }
    },

    clearAll() {
      this.physicalAddress1 = '';
      this.physicalAddress2 = '';
      this.physicalAddressCity = '';
      // this.physicalStateId = '';
      this.physicalAddressZip = '';
      this.physicalAddressZipPlus = '';
      // this.physicalAddressState = '';

      this.mailingAddress1 = '';
      this.mailingAddress2 = '';
      this.mailingAddressCity = '';
      // this.mailingStateId = '';
      this.mailingAddressZip = '';
      this.mailingAddressZipPlus = '';
      // this.mailingAddressState = '';

      this.physicalAddress1ErrMsg = '';
      this.physicalAddressCityErrMsg = '';
      this.physicalAdressStateErrMsg = '';
      this.physicalAddressZipErrMsg = '';

      this.mailingAddress1ErrMsg = '';
      this.mailingAddressCityErrMsg = '';
      this.mailingAdressStateErrMsg = '';
      this.mailingAddressZipErrMsg = '';

      this.physicalMailingAddressCheck = false;

      this.$store.state.guestLogin.physicalAddressMsg = '';
      this.$store.state.guestLogin.mailingAddressMsg = '';
    },

    checkMailingAddress() {
      if(this.physicalMailingAddressCheck) {
        this.mailingAddress1 = this.physicalAddress1;
        this.mailingAddress2 = this.physicalAddress2;
        this.mailingAddressCity = this.physicalAddressCity;
        this.mailingStateId = this.physicalStateId;
        this.mailingAddressState = this.physicalAddressState;
        this.mailingAddressZip = this.physicalAddressZip;
        this.mailingAddressZipPlus = this.physicalAddressZipPlus;

        this.mailingAddress1ErrMsg = '';
        this.mailingAddressCityErrMsg = '';
        this.mailingAdressStateErrMsg = '';
        this.mailingAddressZipErrMsg = '';
      }
    },
    validateAddress(){
      if(this.physicalAddress1 == null || this.physicalAddress1 == '') {
        this.physicalAddress1ErrMsg = 'Adress line1 is required';
      }
      else {
        this.physicalAddress1ErrMsg = '';
      }
    },
    validateCity() {
      if(this.physicalAddressCity == null || this.physicalAddressCity == '') {
        this.physicalAddressCityErrMsg = 'City is required';
      }
      else{
        this.physicalAddressCityErrMsg = '';
      }
    },
    validateState() {
      if(this.physicalStateId == null || this.physicalStateId == '') {
        this.physicalAdressStateErrMsg = 'State is required';
      }
      else{
        this.physicalAdressStateErrMsg = '';
      }
    },
    validateZip(){
      if(this.physicalAddressZip == null || this.physicalAddressZip == '') {
        this.physicalAddressZipErrMsg = 'Zipcode is required';
      }
      else{
        this.physicalAddressZipErrMsg = '';
      }
    },

    validateMailingAddress() {
      if((this.mailingAddress1 == null || this.mailingAddress1 == '') && !this.physicalMailingAddressCheck) {
        this.mailingAddress1ErrMsg = 'Adress line1 is required';
      }
      else {
        this.mailingAddress1ErrMsg = '';
      }
    },
    validateMailingCity() {
      if((this.mailingAddressCity == null || this.mailingAddressCity == '') && !this.physicalMailingAddressCheck) {
        this.mailingAddressCityErrMsg = 'City is required';
      }
      else{
        this.mailingAddressCityErrMsg = '';
      }
    },
    validateMailingState() {
      if((this.mailingStateId == null || this.mailingStateId == '') && !this.physicalMailingAddressCheck) {
        this.mailingAdressStateErrMsg = 'State is required';
      }
      else{
        this.mailingAdressStateErrMsg = '';
      }
    },
    validateMailingZip(){
      if((this.mailingAddressZip == null || this.mailingAddressZip == '') && !this.physicalMailingAddressCheck) {
        this.mailingAddressZipErrMsg = 'Zipcode is required';
      }
      else{
        this.mailingAddressZipErrMsg = '';
      }
    }
  },

  beforeDestroy() {
    this.$store.state.guestLogin.physicalAddressMsg = '';
    this.$store.state.guestLogin.mailingAddressMsg = '';
  }
}
</script>

<style scoped lang="scss">

.app-address-info{
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;

  h2 {
    font-size: 1rem;
    font-family: "Work Sans", sans-serif;
    margin-bottom: 1rem;
  }
  .app-input-address{
    display: block;
    .app-user-address-info{
      display: flex;
      gap: 1rem;
    }
    .app-user-address{
      margin-bottom: 1rem;
      display: flex;
      .user-address-info{
        width: 100%;
      }
      .user-address-info-city, .user-address-info-state{
        width: 200px !important;
      }
      .user-address-info-zip{
        width: 115px;
      }
      .user-address-info-gray{
        background: lightgray;
      }
      .zip-selection {
        width: 106px !important;
      }
      .user-address-info-zipplus {
        max-width: 100px;
      }
      select {
        .app-custom-select {
          max-width: 90%;
        }
      }
      .diff{
        padding: 0.5rem;
      }
      .add-line{
        width: 100%;
      }
    }
  }
}

.phymailcheck{
  margin: 1rem 0 1rem 0;
  span{
    margin-left: 1rem;
  }
}
.btnClear{
  margin: 1rem 0 1rem 0;
}

.err-msg{
  color: var(--ri-red);
}
.zip-error{
  margin-top: -1rem;
}
@media only screen and (max-width: 510px) {
  .app-address-info{
    display: flex;
    flex-wrap: wrap;
    gap: 2rem;
    .app-input-address{
      display: block;
      .app-user-address-info{
        display: block;
      }
      .app-user-address{
        margin-bottom: 1rem;
        .user-address-info{
          min-width: 300px;
        }
        .user-address-info-state, .user-address-info-zip{
          display: block;
        }
        .user-address-info-city {
          width: 200px;
        }
        .zip-selection {
          width: 106px !important;
        }
        .user-address-info-zipplus {
          max-width: 100px;
        }
      }
    }
  }
}
</style>